import React, { useState, useEffect, useCallback } from 'react';
import { Card, Typography, Skeleton, message, Tabs, Button, Switch, Select, InputNumber, Descriptions } from 'antd';
import { getAllApplications, addGradeToApplication, updateApplicationRounds } from '../../api/application';
import { getCurrentGrading } from '../../api/user';
import { fetchProvinces } from '../../api/competition';
import { useSettings } from '../../contexts/settingsContext';
import { getVenues } from '../../api/venue';
import { loadState } from '../../stores/store';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import debounce from 'lodash/debounce';
import { generateGradingSheetPDF, exportGradedApplicationsToXLSX } from '../../utils/gradingUtils';
import GradingTable from '../../components/Grading/gradingTable';
import { io as socketIOClient } from 'socket.io-client';
import MedalWinnersModal from '../../components/Grading/medalWinnersModal';
import './judging.scss';

dayjs.extend(utc);
dayjs.extend(timezone);

const { Title } = Typography;

const Judging = () => {
    const { state: settings } = useSettings();
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [venues, setVenues] = useState([]);
    const [selectedVenue, setSelectedVenue] = useState(null);
    const [grades, setGrades] = useState({});
    const [gradingFinished, setGradingFinished] = useState(false);
    const [provinces, setProvinces] = useState([]);
    const [provinceFilter, setProvinceFilter] = useState(null);

    const state = loadState();
    const user = state.user;

    const [medalWinnersVisible, setMedalWinnersVisible] = useState(false);
    const [openApplicationId, setOpenApplicationId] = useState(null);
    const [assignedApplication, setAssignedApplication] = useState(null);
    const [assignedGrades, setAssignedGrades] = useState({});

    const medalTitleMap = {
        'gold-row': 'Gold',
        'silver-row': 'Silver',
        'bronze-row': 'Bronze'
    };

    const mapSizeToType = (size) => {
        if (size === 2) return 'duet';
        if (size === 3) return 'trio';
        if (size >= 4 && size <= 9) return 'small group';
        if (size >= 10 && size <= 19) return 'large group';
        if (size >= 20) return 'production line';
        return 'solo';
    };

    const fetchApplications = async () => {
        try {
            // If the user is a judge, fetch only their currently assigned grading application
            let currentApplications = [];
            if (state.user && state.user.role === 'judge') {
                const resp = await getCurrentGrading();
                if (resp && resp.application) {
                    currentApplications = [resp.application];
                } else {
                    currentApplications = [];
                }
            } else {
                const applicationsData = await getAllApplications(settings.selectedRound);
                currentApplications = applicationsData
                    .filter((app) => app.round === settings.selectedRound)
                    .sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort by date 
            }
            
            const fetchedProvinces = await fetchProvinces(settings.selectedCompetition._id);
            setProvinces(fetchedProvinces);

            let filteredApplications = currentApplications;

            if (provinceFilter && provinceFilter !== "all") {
                filteredApplications = currentApplications.filter(
                    a => a.province === provinceFilter
                );
            }

            // Pre-populate grades  
            const initialGrades = {};
            filteredApplications.forEach((app) => {
                const userGrades = app.grades.find((grade) => grade.graded_by === state.user._id);
                if (userGrades) {
                    initialGrades[app._id] = userGrades;
                }
            });

            setGrades(initialGrades);
            setApplications(filteredApplications);
            setLoading(false);

        } catch (error) {
            console.error(`Failed to load applications: ${error}`);
            message.error('Failed to load applications.');
            setLoading(false);
        }
    };

    const fetchVenues = async () => {
        try {
            const venuesData = await getVenues();
            setVenues(venuesData);
            if (venuesData.length > 0) {
                setSelectedVenue(venuesData[0].venue); // Default to the first venue    
            }
        } catch (error) {
            message.error('Failed to load venues.');
        }
    };

    useEffect(() => {
        fetchApplications();
        fetchVenues();
    }, [settings.selectedRound, state.user._id, provinceFilter]);

    // socket setup for judges/admin receiving grading sheets
    useEffect(() => {
        const socket = socketIOClient();
        if (state.user && state.user._id) {
            socket.on('connect', () => {
                socket.emit('register', state.user._id);
            });

            socket.on('openGradingSheet', (payload) => {
                if (!payload || !payload.application) return;
                const app = payload.application;
                // For judges, set assignedApplication; for admins we'll still open modal via openApplicationId
                if (state.user.role === 'judge') {
                    setAssignedApplication(app);
                    // Pre-fill assignedGrades if this user has already graded this app
                    try {
                        const userGrade = (app.grades || []).find(g => g.graded_by === state.user._id);
                        if (userGrade) {
                            // copy only criteria
                            const initial = {
                                entertainment: userGrade.entertainment,
                                technicality: userGrade.technicality,
                                style: userGrade.style,
                                stage_presence: userGrade.stage_presence,
                                originality: userGrade.originality,
                            };
                            setAssignedGrades(initial);
                        }
                    } catch (e) {
                        // ignore
                    }
                } else {
                    setOpenApplicationId(app._id);
                }
            });

            // Listen for fully graded notifications so admin UI can update the application as fully graded
            socket.on('applicationFullyGraded', (payload) => {
                try {
                    if (!payload || !payload.application) return;
                    const updatedApp = payload.application;
                    // Set both snake_case (from server) and camelCase for legacy UI usage
                    setApplications((prev) => prev.map((a) => (a._id === updatedApp._id ? { ...a, ...updatedApp, fullyGraded: updatedApp.fully_graded || updatedApp.fullyGraded } : a)));
                    message.success('An application has been fully graded.');
                } catch (e) {
                    console.error('Error handling applicationFullyGraded socket event', e);
                }
            });
        }

        return () => {
            try { socket.disconnect(); } catch (e) {}
        };
    }, [state.user]);

    // On mount for judges, fetch their current assigned grading application
    useEffect(() => {
        const loadCurrent = async () => {
            if (state.user && state.user.role === 'judge') {
                try {
                    const resp = await getCurrentGrading();
                    if (resp && resp.application) {
                        setAssignedApplication(resp.application);
                        try {
                            const app = resp.application;
                            const userGrade = (app.grades || []).find(g => g.graded_by === state.user._id);
                            if (userGrade) {
                                setAssignedGrades({
                                    entertainment: userGrade.entertainment,
                                    technicality: userGrade.technicality,
                                    style: userGrade.style,
                                    stage_presence: userGrade.stage_presence,
                                    originality: userGrade.originality,
                                });
                            }
                        } catch (e) {}
                    } else {
                        setAssignedApplication(null);
                    }
                } catch (e) {
                    console.error('Failed to load current grading assignment', e);
                }
            }
        };
        loadCurrent();
    }, [state.user]);

    const handleTabChange = (venue) => {
        setSelectedVenue(venue);
    };

    const debouncedAddGrade = useCallback(
        debounce(async (applicationId, grades) => {
            try {
                await addGradeToApplication(applicationId, grades);

                // Update the applications state to include the new grade    
                setApplications(prevApplications =>
                    prevApplications.map(app => {
                        if (app._id === applicationId) {
                            // Replace any existing grade by the current user    
                            const existingGrades = app.grades.filter(grade => grade.graded_by !== state.user._id);
                            return {
                                ...app,
                                grades: [...existingGrades, { ...grades, graded_by: state.user._id }]
                            };
                        }
                        return app;
                    })
                );

                message.success('Grade added successfully');
                // If grader is a judge, refresh applications (their assignment may have been cleared)
                if (state.user && state.user.role === 'judge') {
                    fetchApplications();
                }
            } catch (error) {
                console.error('Error adding grade:', error);
                message.error('Failed to add grade');
            }
        }, 500),
        [state.user._id]
    );

    const handleGradeChange = (applicationId, field, value) => {
        setGrades((prevGrades) => {
            const updatedGrades = { ...prevGrades, [applicationId]: { ...prevGrades[applicationId], [field]: value } };
            const applicationGrades = updatedGrades[applicationId];
            const requiredFields = ['entertainment', 'technicality', 'style', 'stage_presence', 'originality'];
            const allFieldsFilled = requiredFields.every((field) => applicationGrades[field] !== undefined);
            if (allFieldsFilled) {
                debouncedAddGrade(applicationId, applicationGrades);
            }
            return updatedGrades;
        });
    };

    const calculateFinalGrade = (application) => {
        const applicationGrades = grades[application._id] || {};
        const requiredFields = ['entertainment', 'technicality', 'style', 'stage_presence', 'originality'];
        return requiredFields.reduce((sum, field) => sum + (applicationGrades[field] || 0), 0);
    };

    const calculateAverageFinalGrade = (application) => {
        const applicationGrades = application.grades || [];
        if (applicationGrades.length === 0) return null;
        const criteria = ['entertainment', 'technicality', 'style', 'stage_presence', 'originality'];
        // Calculate average for each criterion    
        const criteriaAverages = criteria.map((criterion) => {
            const total = applicationGrades.reduce((acc, grade) => acc + (grade[criterion] || 0), 0);
            return total / applicationGrades.length;
        });
        // Sum up the averages of all criteria    
        const totalAverageGrade = criteriaAverages.reduce((sum, avg) => sum + avg, 0);
        return totalAverageGrade;
    };

    const judgeRequiredFields = ['entertainment', 'technicality', 'style', 'stage_presence', 'originality'];
    const assignedAllFieldsFilled = judgeRequiredFields.every((f) => assignedGrades[f] !== undefined && assignedGrades[f] !== null);

    const filteredApplications = applications.filter(
        (app) => app.event && app.event.venue && app.event.venue.venue === selectedVenue
    );

    const handleExportGradedApplications = () => {
        const gradedApplications = gradingFinished
            ? filteredApplications
            : filteredApplications.filter(app => grades[app._id]);
        exportGradedApplicationsToXLSX(gradedApplications, grades, selectedVenue, user, gradingFinished);
    };

    const groupByEventStyle = (applications) => {
        return applications.reduce((acc, app) => {
            const eventStyle = app.event.style;
            if (!acc[eventStyle]) {
                acc[eventStyle] = [];
            }
            acc[eventStyle].push(app);
            return acc;
        }, {});
    };

    // Group applications by event style    
    const groupedApplications = groupByEventStyle(filteredApplications);

    // Compute medal assignments    
    const medalAssignments = {};
    Object.keys(groupedApplications).forEach(eventStyle => {
        const styleGroup = groupedApplications[eventStyle];
        const sortedStyleGroup = [...styleGroup].sort(
            (a, b) => calculateAverageFinalGrade(b) - calculateAverageFinalGrade(a)
        );
        // Variables to track medal awarding    
        let medalAwarded = null;
        // Iterate over the top 3 performers    
        for (let i = 0; i < sortedStyleGroup.length && i < 3; i++) {
            const application = sortedStyleGroup[i];
            const avgGrade = calculateAverageFinalGrade(application);
            if (i === 0) {
                if (avgGrade >= settings.gradingDetails.goldMedalGrade) {
                    medalAssignments[application._id] = 'gold-row';
                    medalAwarded = 'gold';
                } else {
                    // No gold medal awarded    
                    medalAwarded = null;
                }
            } else if (i === 1) {
                if (medalAwarded === 'gold' && avgGrade >= settings.gradingDetails.silverMedalGrade) {
                    medalAssignments[application._id] = 'silver-row';
                    medalAwarded = 'silver';
                } else {
                    // No silver medal awarded    
                    medalAwarded = null;
                }
            } else if (i === 2) {
                if (medalAwarded === 'silver' && avgGrade >= settings.gradingDetails.bronzeMedalGrade) {
                    medalAssignments[application._id] = 'bronze-row';
                    medalAwarded = 'bronze';
                } else {
                    // No bronze medal awarded    
                }
            }
        }
    });

    // Function to determine the row class name based on passing grade    
    const getRowClassName = (record) => {
        if (gradingFinished) {
            const averageFinalGrade = calculateAverageFinalGrade(record);
            if (averageFinalGrade >= settings.gradingDetails.passingGrade) {
                return 'pass-row';
            } else {
                return 'fail-row';
            }
        }
        return '';
    };

    const getMedalWinners = () => {
        const medalWinners = [];
        Object.keys(medalAssignments).forEach(applicationId => {
            const application = filteredApplications.find(app => app._id === applicationId);
            if (application) {
                let contestantName;
                if (application.contestant) {
                    contestantName = `${application.contestant.first_name} ${application.contestant.last_name}`;
                } else if (application.group) {
                    contestantName = application.group.group_name;
                }
                medalWinners.push({
                    ...application,
                    medal: medalTitleMap[medalAssignments[applicationId]],
                    contestantName: contestantName // Add the full name(s) here    
                });
            }
        });
        return medalWinners;
    };

    const medalWinners = getMedalWinners();

    const handleDuplicatePassedApplications = async () => {
        setLoading(true);
        try {
            // Get all applications that have passed  
            const passedApplications = filteredApplications.filter((app) => {
                const avgGrade = calculateAverageFinalGrade(app);
                return avgGrade !== null && avgGrade >= settings.gradingDetails.passingGrade;
            });

            // Extract their IDs  
            const applicationIds = passedApplications.map((app) => app._id);

            if (applicationIds.length === 0) {
                message.info('No passing applications.');
                setLoading(false);
                return;
            }

            // Call the API to duplicate applications  
            await updateApplicationRounds(applicationIds);

            message.success('Passing applications successfully moved.');

            // Optionally, you can refresh the applications list here  
            // fetchApplications();  
        } catch (error) {
            console.error('Error duplicating applications:', error);
            message.error('Failed to duplicate passed applications.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="schedule-container">
            <Title level={4}>Judging</Title>
            <Card bordered={false} className="applications-card" style={{ position: 'relative' }}>
            {(!(state.user && state.user.role === 'judge')) && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 16, marginBottom: 16 }}>
                    <Select
                        value={provinceFilter}
                        onChange={value => setProvinceFilter(value)}
                        style={{ width: 250 }}
                        showSearch
                        allowClear
                        placeholder="Select a province"
                    >
                        <Select.Option value="">All</Select.Option>
                        {provinces && Array.isArray(provinces) &&
                            provinces.map(prov => (
                                <Select.Option key={prov._id} value={prov._id}>
                                    {prov.province} ({prov.location}) - {prov.date}
                                </Select.Option>
                            ))
                        }
                    </Select>
                </div>
            )}
                {loading ? (
                    <Skeleton active />
                ) : (
                    <>
                        {(!(state.user && state.user.role === 'judge')) && (
                            <>
                                <Tabs defaultActiveKey={selectedVenue} onChange={handleTabChange}>
                                    {venues.map((venue) => (
                                        <Tabs.TabPane tab={venue.venue} key={venue.venue}></Tabs.TabPane>
                                    ))}
                                </Tabs>
                                <Button
                                    type="primary"
                                    style={{ marginBottom: 16 }}
                                    onClick={() => generateGradingSheetPDF(filteredApplications, selectedVenue)}
                                >
                                    Generate Grading Sheet PDF
                                </Button>
                                <Button
                                    type="primary"
                                    style={{ marginLeft: 8 }}
                                    onClick={handleExportGradedApplications}
                                >
                                    Export Applications to XLSX
                                </Button>
                                <Button
                                    type="primary"
                                    style={{ marginLeft: 8 }}
                                    onClick={() => setMedalWinnersVisible(true)}
                                >
                                    Show Medal Winners
                                </Button>
                                <Button
                                    type="primary"
                                    style={{ marginLeft: 8 }}
                                    onClick={handleDuplicatePassedApplications}
                                >
                                    Move Passing Applications to Next Round
                                </Button>
                            </>
                        )}
                        {state.user && state.user.role === 'judge' ? (
                            // Judge view: show only the assigned application grading sheet
                            <>
                                {assignedApplication ? (
                                    <Card className="judge-assignment-card" style={{ maxWidth: 1000, margin: 0 }}>
                                        <div className="assignment-meta" style={{ marginBottom: 12 }}>
                                            <Descriptions size="small" column={1} bordered>
                                                <Descriptions.Item label="Participants">
                                                    {assignedApplication.group?.group_name || (assignedApplication.contestant ? `${assignedApplication.contestant.first_name} ${assignedApplication.contestant.last_name}` : assignedApplication.contestants?.map(c => `${c.first_name} ${c.last_name}`).join(', ') || 'No Contestants')}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Event">
                                                    {assignedApplication.event?.name} - {assignedApplication.event?.style}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Type">
                                                    {assignedApplication.group ? mapSizeToType(assignedApplication.group.group_size) : 'solo'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Scheduled Time">
                                                    {assignedApplication.date ? dayjs.utc(assignedApplication.date).tz('Europe/Bucharest').format('YYYY-MM-DD HH:mm') : 'Not Scheduled'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Province">
                                                    {assignedApplication.province?.province || (assignedApplication.province ? (assignedApplication.province.province + ' (' + (assignedApplication.province.location || '') + ')') : '')}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Age Group">
                                                    {assignedApplication.group_age || 'N/A'}
                                                </Descriptions.Item>
                                                <Descriptions.Item label="Instrument">
                                                    {assignedApplication.instrument || 'N/A'}
                                                </Descriptions.Item>
                                            </Descriptions>
                                        </div>
                                        <div className="criteria-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 12, marginBottom: 12 }}>
                                            {['entertainment', 'technicality', 'style', 'stage_presence', 'originality'].map((crit) => (
                                                <div key={crit} style={{ textAlign: 'center' }}>
                                                    <div style={{ textTransform: 'capitalize', marginBottom: 6 }}>{crit.replace('_', ' ')}</div>
                                                    <InputNumber min={0} max={20} value={assignedGrades[crit]} onChange={(v) => { console.debug('InputNumber change', crit, v); setAssignedGrades(prev => ({ ...prev, [crit]: v })); }} />
                                                </div>
                                            ))}
                                        </div>
                                        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                                            <Button type="primary" className="submit-grade-btn" disabled={!assignedAllFieldsFilled} onClick={async () => {
                                                const required = ['entertainment', 'technicality', 'style', 'stage_presence', 'originality'];
                                                const allFilled = required.every(f => assignedGrades[f] !== undefined && assignedGrades[f] !== null);
                                                if (!allFilled) {
                                                    message.error('Please fill all grading fields');
                                                    return;
                                                }
                                                try {
                                                    await addGradeToApplication(assignedApplication._id, assignedGrades);
                                                    message.success('Grade submitted');
                                                    // Clear local assignment so judge can't see previous grades
                                                    setAssignedApplication(null);
                                                    setAssignedGrades({});
                                                    // Refresh server-side assignment (server clears currentGradingApplication)
                                                    try { const resp = await getCurrentGrading(); if (!resp || !resp.application) { /* nothing */ } } catch(e) {}
                                                } catch (e) {
                                                    console.error('Failed to submit grade', e);
                                                    message.error('Failed to submit grade');
                                                }
                                            }}>Submit Grade</Button>
                                        </div>
                                    </Card>
                                ) : (
                                    <Card>
                                        <div>No grading assignment at the moment.</div>
                                    </Card>
                                )}
                            </>
                        ) : (
                            <GradingTable
                                filteredApplications={filteredApplications}
                                getRowClassName={(record) => {
                                    if (!gradingFinished) {
                                        return '';
                                    }
                                    const baseClassName = getRowClassName(record); // 'pass-row' or 'fail-row'    
                                    const medalClassName = medalAssignments[record._id] || '';
                                    return `${baseClassName} ${medalClassName}`.trim();
                                }}
                                gradingFinished={gradingFinished}
                                grades={grades}
                                handleGradeChange={handleGradeChange}
                                userRole={state.user?.role}
                                calculateFinalGrade={calculateFinalGrade}
                                calculateAverageFinalGrade={calculateAverageFinalGrade}
                                mapSizeToType={mapSizeToType}
                                settings={settings}
                                medalWinners={medalWinners}
                                openApplicationId={openApplicationId}
                            />
                        )}
                        {(!(state.user && state.user.role === 'judge')) && (
                            <MedalWinnersModal
                                visible={medalWinnersVisible}
                                onCancel={() => setMedalWinnersVisible(false)}
                                medalWinners={medalWinners}
                                gradingFinished={gradingFinished}
                            />
                        )}
                    </>
                )}
            </Card>
        </div>
    );
};

export default Judging;  